import { env } from './env';
import { logger } from './logger';

export interface ModelConfig {
  name: string;
  supportsTemperature: boolean;
  supportsMaxTokens: boolean;
  maxTokensField: 'max_tokens' | 'max_completion_tokens';
  defaultMaxTokens: number;
  fallbackModel?: string;
}

const MODEL_CONFIGS: Record<string, ModelConfig> = {
  'gpt-5': {
    name: 'gpt-5',
    supportsTemperature: false,
    supportsMaxTokens: true,
    maxTokensField: 'max_completion_tokens',
    defaultMaxTokens: 4096,
    fallbackModel: 'gpt-4o'
  },
  'gpt-4o': {
    name: 'gpt-4o',
    supportsTemperature: true,
    supportsMaxTokens: true,
    maxTokensField: 'max_completion_tokens',
    defaultMaxTokens: 4096,
    fallbackModel: 'gpt-4-turbo'
  },
  'gpt-4-turbo': {
    name: 'gpt-4-turbo',
    supportsTemperature: true,
    supportsMaxTokens: true,
    maxTokensField: 'max_completion_tokens',
    defaultMaxTokens: 4096,
    fallbackModel: 'gpt-4'
  },
  'gpt-4': {
    name: 'gpt-4',
    supportsTemperature: true,
    supportsMaxTokens: true,
    maxTokensField: 'max_tokens',
    defaultMaxTokens: 4096
  }
};

class ModelConfigManager {
  private static instance: ModelConfigManager;
  private currentModel: string;
  private currentConfig: ModelConfig;

  private constructor() {
    this.currentModel = env.VITE_OPENAI_MODEL || 'gpt-4o';
    this.currentConfig = this.loadModelConfig(this.currentModel);
    logger.info(`Initialized with model: ${this.currentModel}`);
  }

  public static getInstance(): ModelConfigManager {
    if (!ModelConfigManager.instance) {
      ModelConfigManager.instance = new ModelConfigManager();
    }
    return ModelConfigManager.instance;
  }

  private loadModelConfig(modelName: string): ModelConfig {
    const config = MODEL_CONFIGS[modelName];
    if (!config) {
      logger.warn(`Unknown model ${modelName}, using gpt-4o config as fallback`);
      return MODEL_CONFIGS['gpt-4o'];
    }
    return config;
  }

  public getModelName(): string {
    return this.currentConfig.name;
  }

  public getConfig(): ModelConfig {
    return { ...this.currentConfig };
  }

  public buildCompletionParams(params: {
    messages: Array<{ role: string; content: string }>;
    temperature?: number;
    maxTokens?: number;
  }): any {
    const completionParams: any = {
      model: this.currentConfig.name,
      messages: params.messages
    };

    if (this.currentConfig.supportsTemperature && params.temperature !== undefined) {
      completionParams.temperature = params.temperature;
    }

    if (this.currentConfig.supportsMaxTokens && params.maxTokens !== undefined) {
      completionParams[this.currentConfig.maxTokensField] = params.maxTokens;
    }

    return completionParams;
  }

  public getFallbackModel(): string | undefined {
    return this.currentConfig.fallbackModel;
  }

  public switchToFallback(): boolean {
    const fallback = this.currentConfig.fallbackModel;
    if (!fallback) {
      logger.error('No fallback model available');
      return false;
    }

    logger.warn(`Switching from ${this.currentModel} to fallback model ${fallback}`);
    this.currentModel = fallback;
    this.currentConfig = this.loadModelConfig(fallback);
    return true;
  }

  public isModelAvailable(modelName: string): boolean {
    return MODEL_CONFIGS.hasOwnProperty(modelName);
  }

  public setModel(modelName: string): boolean {
    if (!this.isModelAvailable(modelName)) {
      logger.error(`Model ${modelName} is not configured`);
      return false;
    }

    this.currentModel = modelName;
    this.currentConfig = this.loadModelConfig(modelName);
    logger.info(`Switched to model: ${modelName}`);
    return true;
  }
}

export const modelConfig = ModelConfigManager.getInstance();
